import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { TWO_STEP_ROLES, type Role } from "@clevis/shared-types";
import { cn } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";
import { useAuth } from "../hooks/useAuth";

// Team → a person → whether they sign in with two steps; the owner can reset
// it for someone who lost their phone (they sign in with their password and
// set it up again).

const cache = new Map<string, { at: number; p: Promise<Record<string, number>> }>();
function statusFor(tenantId: string, fresh = false): Promise<Record<string, number>> {
  const hit = cache.get(tenantId);
  if (hit && !fresh && Date.now() - hit.at < 60_000) return hit.p;
  const p = call<{ factors: Record<string, number> }>("getTwoStepStatus", { tenantId }, { mutation: false }).then((r) => r.factors);
  cache.set(tenantId, { at: Date.now(), p });
  p.catch(() => cache.delete(tenantId));
  return p;
}

export function TwoStepTeamLine({ tenantId, uid, name, role, isSelf }: { tenantId: string; uid: string; name: string; role: Role; isSelf: boolean }) {
  const { role: myRole, superAdmin } = useAuth();
  const [count, setCount] = useState<number | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    void statusFor(tenantId).then((f) => setCount(f[uid] ?? 0)).catch(() => setCount(null));
  }, [tenantId, uid]);
  if (count === null) return null;
  const needs = TWO_STEP_ROLES.includes(role);
  const canReset = (myRole === "owner" || superAdmin) && !isSelf && count > 0;

  async function reset() {
    setBusy(true);
    setMsg("");
    try {
      await call("resetTwoStep", { tenantId, uid });
      setCount(0);
      setConfirm(false);
      void statusFor(tenantId, true);
      setMsg(`Cleared. ${name} signs in with their password and sets it up again.`);
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sm:col-span-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <ShieldCheck size={14} className={count ? "text-success" : needs ? "text-brandorange" : "text-steel"} />
        <span className={cn(count ? "text-ink" : needs ? "text-brandorange-dark" : "text-steel")}>
          {count ? "Two-step sign-in is on" : needs ? "Two-step sign-in not set up yet (owners and admins need it)" : "Two-step sign-in off (optional for this role)"}
        </span>
        {canReset ? (
          confirm ? (
            <span className="flex items-center gap-2">
              Reset it? Use this if they lost their phone.
              <button className="font-semibold text-danger hover:underline" disabled={busy} onClick={() => void reset()}>
                Reset
              </button>
              <button className="text-steel hover:text-ink" onClick={() => setConfirm(false)}>
                Keep
              </button>
            </span>
          ) : (
            <button className="text-steel underline hover:text-ink" onClick={() => setConfirm(true)}>
              Lost their phone? Reset
            </button>
          )
        ) : null}
      </div>
      {msg ? <p className="mt-1 text-xs text-steel">{msg}</p> : null}
    </div>
  );
}
